import {AwesomeIcon, ChangeFunction, Loader, SelectItemShape, stopPropagation} from '@dvrd/dvr-controls'
import BolRating from '../../bolRating/bolRating'
import {BolmateSelect} from '../../controls'
import './style/productReviews.scss'
import React, {MouseEventHandler, useEffect, useMemo, useRef, useState} from 'react'
import {ReviewProduct} from '../../../util/interfaces'
import {Rating} from "../../../util/types";
import {elementIsInView, sum} from "../../../util/utils";
import {DotsLoader} from "../../controls/loader/dotsLoader";
import ProductReview from "../../../models/productReviewModel";
import ReviewCounts from '../reviewCounts/reviewCounts'
import Image from "../../misc/image";
import RouterLink from "../../controls/link/routerLink";
import ReviewComponent from "../reviewComponent";
import {DefaultNumberObject} from "../../../util/defaultObject";
import InfoContainer from "../../misc/infoContainer";

interface Props {
    onChangeSorting: ChangeFunction<string>;
    onClickBack: MouseEventHandler;
    onLoadMore: (callback: VoidFunction) => void;
    onSelectRating: (value: number) => ChangeFunction<boolean>;
    reviews: Array<ProductReview>;
    sortingValue: string;
    loadingProduct: boolean;
    loadingRatings: boolean;
    loadingReviews: boolean;
    total: number;
    product: ReviewProduct | null;
    reviewCounts: DefaultNumberObject<Rating>;
    selectedRatings: Set<Rating>;
}

const sortItems: Array<SelectItemShape> = [
    {label: 'Meeste sterren', value: 'MAX_STARS',},
    {label: 'Minste sterren', value: 'MIN_STARS',},
    {label: 'Nieuwste', value: 'MOST_RECENT',},
    {label: 'Meest behulpzaam', value: 'MOST_USEFUL'}
]

export default function ProductReviews(props: Props) {
    const {
        reviews, onChangeSorting, onLoadMore, total, sortingValue, loadingProduct, loadingReviews,
        loadingRatings, onClickBack, product, reviewCounts, onSelectRating, selectedRatings,
    } = props;
    const [loadingMore, setLoadingMore] = useState(false);
    const totalRatings = useMemo(() => {
        return sum(...reviewCounts.values());
    }, [reviewCounts]);
    const hasMore = useMemo(() => {
        return reviews.length < total;
    }, [reviews, total]);
    const scrolling = useRef(false);

    function shouldLoadMore(): boolean {
        if (!hasMore) return false;
        if (!reviews.length) return true;
        const checkIndex = Math.max(0, reviews.length - 3);
        const review = reviews[checkIndex];
        if (!review) return true;
        const element = document.getElementById(`review-${review.id}`);
        return !element || elementIsInView(element);
    }

    function scrollListener() {
        if (loadingMore || scrolling.current) return;
        setLoadingMore(true);
        scrolling.current = true;
        window.requestAnimationFrame(() => {
            if (shouldLoadMore()) onLoadMore(() => {
                setLoadingMore(false);
            });
            else setLoadingMore(false);
            scrolling.current = false;
        });
    }

    function renderReview(review: ProductReview, index: number) {
        return (
            <ReviewComponent review={review} key={index} className='product-review'/>
        )
    }

    function renderBackButton() {
        return (
            <div className='back-button' onClick={onClickBack}>
                <AwesomeIcon name='arrow-left'/>
            </div>
        );
    }

    function renderProduct() {
        if (!product) {
            if (loadingProduct) return <label>Product wordt geladen...</label>
            return <label>Product kon niet worden geladen</label>;
        }
        return (
            <div className='product-container'>
                <Loader active={loadingProduct}/>
                <img className='image-container' src={product.image_url} alt=''/>
                <div className='product-info-container'>
                    <label className='title-text'>{product.title}</label>
                    <label className='subtitle-text'>EAN: {product.ean}</label>
                    <div className='rating'>
                        <label>{product.rating}</label>
                        <BolRating value={product.rating}/>
                    </div>
                    <div className='row'>
                        <div className='nps-container'>
                            <label className='nps-label'>NPS:</label>
                            <div className='nps-score'>
                                <span className='nps'>{product.nps_score}</span>
                            </div>
                            <InfoContainer className='nps-info'>
                                De NPS (net promoter score) geeft aan hoe waarschijnlijk jouw klanten het product dat ze
                                kochten aan zouden raden aan anderen. Deze score wordt berekend door het percentage
                                positieve reviews (5 sterren) te verminderen met het percentage negatieve reviews
                                (1, 2 of 3 sterren). Deze score loopt van -100 t/m 100, waarbij -100 aangeeft dat er
                                alleen maar negatieve reviews zijn en 100 aangeeft dat er alleen maar positieve reviews
                                zijn.
                            </InfoContainer>
                        </div>
                        <div className='link-container'>
                            <RouterLink
                                to={`https://partner.bol.com/sdd/product-overview/products/${product.global_id}`}
                                target='_blank' title='Bekijk product in jouw verkoopaccount'
                                onClick={stopPropagation}>
                                <Image name='bol-16' alt='' className='logo-link'/>
                            </RouterLink>
                            <RouterLink
                                to={`https://www.bol.com/nl/nl/p/${product.title.replace('/',
                                    '-')}/${product.global_id}#product-reviews`}
                                target='_blank' title='Bekijk product op bol' onClick={stopPropagation}>
                                <Image name='bol-fav-32' alt='' className='logo-link'/>
                            </RouterLink>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderReviews() {
        return (
            <>
                <div className='sort-container'>
                    <BolmateSelect value={sortingValue} onChange={onChangeSorting} items={sortItems} label='Sorteer op'
                                   itemsHeight='30rem' highlightSelected disabled={loadingReviews} dynamicWidth={true}/>
                </div>
                <div className='reviews-container'>
                    {reviews.map(renderReview)}
                    {(!reviews.length && !loadingReviews) &&
                        <label className='empty-label'>Dit product heeft nog geen reviews</label>}
                    {(loadingReviews || loadingMore) && <DotsLoader dotSize='4rem' className='reviews-loader' noSmile/>}
                </div>
            </>
        )
    }

    useEffect(() => {
        if (hasMore)
            document.addEventListener('scroll', scrollListener, {passive: true});

        return function() {
            document.removeEventListener('scroll', scrollListener);
        }
    }, [hasMore, reviews, loadingMore]);

    return (
        <>
            <div className='product-reviews-container'>
                {renderBackButton()}
                {renderProduct()}
                <ReviewCounts onSelectRating={onSelectRating} loading={loadingRatings} total={totalRatings}
                              counts={reviewCounts.toRecord()} selectedRatings={selectedRatings}/>
                {renderReviews()}
            </div>
        </>
    )
}